import type { ComponentPublicInstance, InjectionKey, Ref } from 'vue'
import {
    computed,
    ref,
    shallowRef,
    watch,
    onMounted,
    provide,
    inject,
    onBeforeRouteLeave,
    useEventListener,
    useIntersectionObserver,
    useWindowScroll,
    useThrottleFn,
    useDocumentVisibility,
    useExperimentAssignment,
    useIsAbTest,
    useTracking,
    useApmLabels,
    useCookieConsent,
    usePreferredReducedMotion,
    versionedEventName,
} from '#imports'
import { ExperimentName, ExperimentCookieName } from '../types'
import type { VideoAutoplayState } from './useDeferredVideoAutoplay'

type CtaElement = 'hero' | 'nav' | 'sticky'

export interface LandingTrackingReturn {
    observeCta: (el: Element | ComponentPublicInstance | null) => void
    trackCtaClick: (element: CtaElement) => void
    trackLoginClick: () => void
    observeVideo: (video: Ref<HTMLVideoElement | null | undefined>, autoplay: Readonly<Ref<VideoAutoplayState | null>>) => void
    markIntroDone: () => void
}

export const LandingTrackingKey: InjectionKey<LandingTrackingReturn> = Symbol('landing-tracking')

export const NOOP_LANDING_TRACKING: LandingTrackingReturn = {
    observeCta: (_el: Element | ComponentPublicInstance | null) => {},
    trackCtaClick: (_element: CtaElement) => {},
    trackLoginClick: () => {},
    observeVideo: (_video: Ref<HTMLVideoElement | null | undefined>, _autoplay: Readonly<Ref<VideoAutoplayState | null>>) => {},
    markIntroDone: () => {},
}

/**
 * `landing_page_exit` = "page hidden or left": pagehide,
 * visibilitychange:hidden, or onBeforeRouteLeave; at most once; never after a CTA click.
 *
 * Deep-link visitors (?auth=signup) with the signup sheet open never fire `landing_page_seen`
 * because the CTA is display:none while the modal is visible.
 *
 * Call once per page — from the landing page component — and use provide/inject
 * (LandingTrackingKey) to share the result with child components.
 */
export const useLandingTracking = (): LandingTrackingReturn => {
    const isAbTest = useIsAbTest(ExperimentName.LANDING_PAGE)
    const assignment = useExperimentAssignment({ cookieName: ExperimentCookieName.LANDING_PAGE, experimentName: ExperimentName.LANDING_PAGE })
    const { trackEvent } = useTracking()
    const { register } = useApmLabels()
    const { isConsentReady, isDeclined, consent, whenLoaded } = useCookieConsent()

    const landingVersion = computed(() => assignment.value?.version ?? null)

    register(() => (landingVersion.value ? { lp_variant: landingVersion.value } : null))

    if (!isAbTest || !import.meta.client) {
        provide(LandingTrackingKey, NOOP_LANDING_TRACKING)
        return NOOP_LANDING_TRACKING
    }
    if (!landingVersion.value) {
        provide(LandingTrackingKey, NOOP_LANDING_TRACKING)
        return NOOP_LANDING_TRACKING
    }

    const { y: scrollY } = useWindowScroll()
    const visibility = useDocumentVisibility()
    const reducedMotion = usePreferredReducedMotion()

    const ctaEl = shallowRef<HTMLElement | null>(null)
    const seenAt = ref<number | null>(null)
    const ctaClicked = ref(false)
    const maxScrollPct = ref(0)
    const exitSent = ref(false)

    const videoFirstFrameMs = ref<number | null>(null)
    const videoFailed = ref(false)
    let videoAutoplay: Readonly<Ref<VideoAutoplayState | null>> | undefined
    let videoEl: Ref<HTMLVideoElement | null | undefined> | undefined
    const introDoneMs = ref<number | null>(null)
    const sectionEls = shallowRef<HTMLElement[]>([])
    const sectionsSeen = ref<string[]>([])

    // Throttled so the layout read (scrollHeight) doesn't run on every scroll event
    const updateMaxScroll = useThrottleFn(() => {
        const docH = document.documentElement.scrollHeight - window.innerHeight
        if (docH <= 0) return
        const pct = Math.round((scrollY.value / docH) * 100)
        if (pct <= maxScrollPct.value) return
        maxScrollPct.value = pct
    }, 200, true)
    watch(scrollY, updateMaxScroll)

    const { stop: stopSeenObserver } = useIntersectionObserver(
        ctaEl,
        (entries) => {
            if (!entries[0]?.isIntersecting || seenAt.value !== null) return

            const now = performance.now()
            seenAt.value = now
            stopSeenObserver()

            void trackEvent({
                event_name: versionedEventName('landing_page_seen', landingVersion.value),
                event_type: 'Experiment',
                meta_data: [
                    { ms_since_navigation: Math.round(now) },
                    ...(introDoneMs.value !== null ? [{ intro_done_ms: introDoneMs.value }] : []),
                    ...videoMetaData(),
                ],
            })
        },
        { threshold: 0.1 },
    )

    useIntersectionObserver(
        sectionEls,
        (entries) => {
            for (const entry of entries) {
                if (!entry.isIntersecting) continue
                const name = (entry.target as HTMLElement).dataset.lpSection
                if (name && !sectionsSeen.value.includes(name)) {
                    sectionsSeen.value = [...sectionsSeen.value, name]
                }
            }
        },
        { threshold: 0.2 },
    )

    onMounted(() => {
        sectionEls.value = [...document.querySelectorAll<HTMLElement>('[data-lp-section]')]
    })

    // The CMP loads after hydration and its first sync flips isConsentReady for a returning visitor
    // too, so only a flip after it loaded without a stored answer is a decision made on this page view.
    const awaitingDecision = ref(false)
    void whenLoaded(60_000).then((loaded) => {
        if (!loaded) return
        awaitingDecision.value = !isConsentReady.value
    })
    watch(isConsentReady, (ready) => {
        if (!ready || !awaitingDecision.value) return
        awaitingDecision.value = false

        const decision = consentDecision()
        const now = performance.now()
        void trackEvent({
            event_name: versionedEventName('landing_page_consent', landingVersion.value),
            event_type: 'Experiment',
            meta_data: [
                { decision },
                { ms_since_navigation: Math.round(now) },
                ...(seenAt.value !== null ? [{ ms_since_seen: Math.round(now - seenAt.value) }] : []),
            ],
        })
    })

    function consentDecision(): 'all' | 'necessary' | 'custom' {
        if (isDeclined.value) return 'necessary'
        if (consent.value.marketing && consent.value.statistics) return 'all'
        return 'custom'
    }

    function sectionsSeenStr(): string {
        const domOrder = sectionEls.value.map(el => el.dataset.lpSection ?? '')
        return sectionsSeen.value.slice().sort((a, b) => domOrder.indexOf(a) - domOrder.indexOf(b)).join(',')
    }

    // video_autoplay and prefers_reduced_motion let Datahub split the visitor → NU → NDU funnel by
    // blocked autoplay; the APM labels sit on anonymous page-loads only and cannot be joined to a session.
    // video_muted tells a hero that plays with sound apart from its muted fallback.
    function videoMetaData() {
        return [
            ...(videoFirstFrameMs.value !== null ? [{ video_first_frame_ms: videoFirstFrameMs.value }] : []),
            ...(videoFailed.value ? [{ video_failed: true }] : []),
            ...(videoAutoplay?.value ? [{ video_autoplay: videoAutoplay.value }] : []),
            ...(videoEl?.value ? [{ video_muted: videoEl.value.muted }] : []),
            { prefers_reduced_motion: reducedMotion.value === 'reduce' },
        ]
    }

    // @cego/tracking uses fetch without keepalive; hard-unload delivery is best-effort.
    // onBeforeRouteLeave fires before the route changes, so SPA exits are reliable.
    function sendExit() {
        if (exitSent.value || ctaClicked.value) return
        exitSent.value = true
        const sections = sectionsSeenStr()
        void trackEvent({
            event_name: versionedEventName('landing_page_exit', landingVersion.value),
            event_type: 'Experiment',
            meta_data: [
                { ms_on_page: Math.round(performance.now()) },
                { max_scroll_pct: maxScrollPct.value },
                { saw_cta: seenAt.value !== null },
                ...videoMetaData(),
                ...(introDoneMs.value !== null ? [{ intro_done_ms: introDoneMs.value }] : []),
                ...(sections ? [{ sections_seen: sections }] : []),
            ],
        })
    }

    useEventListener(window, 'pagehide', sendExit)
    watch(visibility, (state) => {
        if (state !== 'hidden') return
        sendExit()
    })
    onBeforeRouteLeave(sendExit)

    function observeCta(el: Element | ComponentPublicInstance | null) {
        if (el === null) {
            ctaEl.value = null
            return
        }
        if (el instanceof HTMLElement) {
            ctaEl.value = el
            return
        }
        const instance = el as ComponentPublicInstance
        ctaEl.value = instance.$el instanceof HTMLElement ? instance.$el : null
    }

    function trackCtaClick(element: CtaElement) {
        if (!landingVersion.value) return
        ctaClicked.value = true
        const sections = sectionsSeenStr()
        void trackEvent({
            event_name: versionedEventName('landing_page_cta_click', landingVersion.value),
            event_type: 'Experiment',
            meta_data: [
                { element },
                ...(seenAt.value !== null ? [{ ms_since_seen: Math.round(performance.now() - seenAt.value) }] : []),
                ...videoMetaData(),
                ...(introDoneMs.value !== null ? [{ intro_done_ms: introDoneMs.value }] : []),
                ...(sections ? [{ sections_seen: sections }] : []),
            ],
        })
    }

    function trackLoginClick() {
        void trackEvent({
            event_name: versionedEventName('landing_page_login_click', landingVersion.value),
            event_type: 'Experiment',
            meta_data: [
                ...(seenAt.value !== null ? [{ ms_since_seen: Math.round(performance.now() - seenAt.value) }] : []),
            ],
        })
    }

    // Must be called during setup: the listeners are bound to the calling component's scope.
    function observeVideo(video: Ref<HTMLVideoElement | null | undefined>, autoplay: Readonly<Ref<VideoAutoplayState | null>>) {
        videoEl = video
        videoAutoplay = autoplay
        useEventListener(video, 'playing', () => {
            if (videoFirstFrameMs.value !== null) return
            videoFirstFrameMs.value = Math.round(performance.now())
        })
        useEventListener(video, 'error', () => {
            videoFailed.value = true
        })
    }

    function markIntroDone() {
        if (introDoneMs.value !== null) return
        introDoneMs.value = Math.round(performance.now())
    }

    const result: LandingTrackingReturn = { observeCta, trackCtaClick, trackLoginClick, observeVideo, markIntroDone }
    provide(LandingTrackingKey, result)
    return result
}

/**
 * The hero's side of useLandingTracking: reports the hero video, its autoplay state (from
 * useDeferredVideoAutoplay) and the end of the intro (`introDone` turning true) to the page's
 * tracker and returns the CTA bindings.
 * Call during setup, in a component rendered below the page that called useLandingTracking.
 */
export const useLandingHeroTracking = (
    video: Ref<HTMLVideoElement | null | undefined>,
    introDone: Ref<boolean>,
    autoplay: Readonly<Ref<VideoAutoplayState | null>>,
): Pick<LandingTrackingReturn, 'observeCta' | 'trackCtaClick'> => {
    const { observeCta, trackCtaClick, observeVideo, markIntroDone } = inject(LandingTrackingKey, NOOP_LANDING_TRACKING)

    observeVideo(video, autoplay)
    watch(introDone, (done) => {
        if (!done) return
        markIntroDone()
    })

    return { observeCta, trackCtaClick }
}
